import {
  JOURNAL_ENTRY_STATUS,
  type JournalEntry,
  type JournalLine,
} from "@/apis/gql/types";
import { DateCell } from "@/components/shared/cells/date-cell";
import { ExpandRow } from "@/components/shared/cells/expand-row";
import { RawTextCell } from "@/components/shared/cells/text";
import { DataTableColumnHeader } from "@/components/shared/tables/ColumnHeader";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { formatAmount } from "@/lib/utils";
import type { ColumnDef } from "@tanstack/react-table";

type Row = {
  id: string;
  timestamp: Date | null;
  description: string | null;
  document: JournalEntry["document"] | null;
  documentReference: string | null;
  documentType: string | null;
  status: string | null;
  account:
    | JournalLine["journalAccount"]["entity"]
    | JournalLine["journalAccount"]
    | null;
  accountType: string | null;
  debit: number | null;
  credit: number | null;
  currency: string | null;
  taxCode: JournalLine["taxCode"]; // Add this field
};

export type LedgerRow = Row & { subRows?: LedgerRow[] };

export const mapRowToLedgerRow = (entries: JournalEntry[]): LedgerRow[] => {
  return entries.map((entry) => {
    return {
      id: entry.journalEntryId,
      timestamp: entry.timestamp,
      documentReference: entry.documentReference,
      document: entry.document,
      documentType: entry.documentType,
      description: entry.bookingText,
      status: entry.status,
      account: null,
      accountType: null,
      debit: null,
      credit: null,
      currency: null,
      taxCode: null, // Add this for parent row
      subRows: entry.journalLines.map((line) => {
        const account = line.journalAccount.entity ?? line.journalAccount;
        return {
          id: line.journalLineId,
          timestamp: line.createdAt,
          documentReference: null,
          documentType: null,
          description: null,
          status: null,
          document: null,
          account,
          accountType: line.journalAccount.accountType,
          debit: line.debit,
          credit: line.credit,
          currency: line.currency,
          taxCode: line.taxCode,
        };
      }),
    };
  });
};

const expandColumn: ColumnDef<LedgerRow> = {
  id: "expand",
  size: 45,
  header: () => null,
  cell: ({ row }) => (
    <div className="flex items-center justify-center">
      <ExpandRow row={row} showCount={false} />
    </div>
  ),
};

const timestampColumn: ColumnDef<LedgerRow> = {
  accessorKey: "timestamp",
  id: "timestamp",
  header: (header) => {
    const { t } = useTranslation();
    return <DataTableColumnHeader column={header.column} title={t("date")} />;
  },
  cell: ({ row }) => {
    if (!row.original.timestamp) {
      return null;
    }

    return (
      <div style={{ paddingLeft: `${row.depth * 1}rem` }}>
        <DateCell value={row.original.timestamp} />
      </div>
    );
  },
};

const accountColumn: ColumnDef<LedgerRow> = {
  accessorKey: "account",
  id: "account",
  header: (header) => (
    <DataTableColumnHeader column={header.column} title="Konto" />
  ),
  cell: ({ row }) => {
    const { t } = useTranslation();
    const depth = row.depth;

    if (depth === 0) {
      return (
        <Badge variant="secondary" className="px-1">
          {t("ledger.journal_entries.lines", {
            count: row.original.subRows?.length ?? 0,
          })}
        </Badge>
      );
    }

    const entity = row.original.account;

    if (entity?.__typename === "Account") {
      return (
        <div style={{ paddingLeft: `${depth * 1}rem` }}>
          <RawTextCell value={entity.accountName ?? ""} />
        </div>
      );
    }

    if (entity?.__typename === "Supplier") {
      return (
        <div style={{ paddingLeft: `${depth * 1}rem` }}>
          <RawTextCell value={entity.supplierName ?? ""} />
        </div>
      );
    }

    return (
      <div style={{ paddingLeft: `${depth * 1}rem` }}>
        <RawTextCell value={entity?.name ?? ""} />
      </div>
    );
  },
};

const descriptionColumn: ColumnDef<LedgerRow> = {
  accessorKey: "description",
  id: "description",
  header: (header) => (
    <DataTableColumnHeader column={header.column} title="Beschreibung" />
  ),
  cell: ({ row }) => {
    const depth = row.depth;

    if (depth > 1) {
      return null;
    }

    return (
      <div style={{ paddingLeft: `${row.depth * 1}rem` }}>
        <RawTextCell value={row.original.description ?? ""} />
      </div>
    );
  },
};

const referenceColumn: ColumnDef<LedgerRow> = {
  accessorKey: "documentReference",
  id: "documentReference",
  header: (header) => (
    <DataTableColumnHeader column={header.column} title="Referenz" />
  ),
  cell: ({ row }) => {
    const depth = row.depth;

    if (depth > 1) {
      return null;
    }

    const document = row.original.document;

    if (document?.__typename === "Invoice") {
      return <RawTextCell value={document.invoiceNo ?? ""} />;
    }
  },
};

const debitColumn: ColumnDef<LedgerRow> = {
  accessorKey: "debit",
  id: "debit",
  header: (header) => (
    <DataTableColumnHeader column={header.column} title="Debit" />
  ),
  cell: ({ row }) => {
    const depth = row.depth;

    if (depth === 0) {
      const debits = row.original.subRows?.reduce(
        (acc, row) => acc + (row.debit ?? 0),
        0,
      );
      return (
        <RawTextCell
          value={formatAmount(debits ?? 0, row.original.currency ?? "EUR")}
        />
      );
    }

    if (row.original.debit === null) {
      return (
        <div style={{ paddingLeft: `${row.depth * 1}rem` }}>
          <p>—</p>
        </div>
      );
    }

    return (
      <div style={{ paddingLeft: `${row.depth * 1}rem` }}>
        <RawTextCell
          value={formatAmount(
            row.original.debit,
            row.original.currency ?? "EUR",
          )}
        />
      </div>
    );
  },
};

const creditColumn: ColumnDef<LedgerRow> = {
  accessorKey: "credit",
  id: "credit",
  header: (header) => (
    <DataTableColumnHeader column={header.column} title="Credit" />
  ),
  cell: ({ row }) => {
    const depth = row.depth;

    if (depth === 0) {
      const credits = row.original.subRows?.reduce(
        (acc, row) => acc + (row.credit ?? 0),
        0,
      );
      return (
        <RawTextCell
          value={formatAmount(credits ?? 0, row.original.currency ?? "EUR")}
        />
      );
    }

    if (row.original.credit === null) {
      return (
        <div style={{ paddingLeft: `${row.depth * 1}rem` }}>
          <p>—</p>
        </div>
      );
    }

    return (
      <div style={{ paddingLeft: `${row.depth * 1}rem` }}>
        <RawTextCell
          value={formatAmount(
            row.original.credit,
            row.original.currency ?? "EUR",
          )}
        />
      </div>
    );
  },
};

const buColumn: ColumnDef<LedgerRow> = {
  accessorKey: "bu",
  id: "bu",
  size: 80,
  header: (header) => (
    <DataTableColumnHeader column={header.column} title="BU" />
  ),
  cell: ({ row }) => {
    const depth = row.depth;

    // For parent rows (journal entries), aggregate BU from all lines
    if (depth === 0) {
      const taxCodes =
        row.original.subRows
          ?.map((subRow) => subRow.taxCode)
          .filter(
            (taxCode): taxCode is NonNullable<typeof taxCode> =>
              taxCode !== null,
          ) ?? [];

      const uniqueBuValues = Array.from(
        new Set(taxCodes.map((taxCode) => taxCode.datevBu)),
      );

      const buDisplay = uniqueBuValues.filter(Boolean).join(", ");

      if (buDisplay === "") {
        return <p className="text-gray-400 p-1 text-center">—</p>;
      }

      return (
        <div className="p-1">
          <RawTextCell
            className="font-mono text-xs text-center"
            value={buDisplay}
          />
        </div>
      );
    }

    // For child rows (journal lines), show individual BU
    const taxCode = row.original.taxCode;

    if (!taxCode?.datevBu) {
      return (
        <div style={{ paddingLeft: `${depth * 1}rem` }}>
          <p className="text-gray-400 text-center">—</p>
        </div>
      );
    }

    return (
      <div style={{ paddingLeft: `${depth * 1}rem` }}>
        <RawTextCell
          className="font-mono text-xs text-center"
          value={taxCode.datevBu}
        />
      </div>
    );
  },
};

const statusColumn: ColumnDef<LedgerRow> = {
  accessorKey: "status",
  id: "status",
  header: (header) => (
    <DataTableColumnHeader column={header.column} title="Status" />
  ),
  cell: ({ row }) => {
    const label = row.original.status ?? "";
    if (!label) return null;
    const cls = getLedgerStatusStyle(row.original.status);
    return (
      <Badge variant="outline" className={`gap-1 ${cls} capitalize`}>
        {label}
      </Badge>
    );
  },
};

export const columns: ColumnDef<LedgerRow>[] = [
  expandColumn,
  timestampColumn,
  accountColumn,
  descriptionColumn,
  referenceColumn,
  debitColumn,
  creditColumn,
  buColumn,
  statusColumn,
];

const getLedgerStatusStyle = (status?: string | null) => {
  const s = (status ?? "").toLowerCase();
  switch (s) {
    case JOURNAL_ENTRY_STATUS.POSTED:
      return "bg-green-100 border-green-200 text-green-800";
    case JOURNAL_ENTRY_STATUS.DRAFT:
      return "bg-gray-100 border-gray-200 text-gray-800";
    default:
      return "bg-secondary text-secondary-foreground";
  }
};
